<template>
    <div class="mt-12 space-y-12">
        <div class="space-y-4">
            <h3 class="text-lg font-medium leading-6 text-left no-underline normal-case non-italic">IRA Owner Information</h3>
            <div class="text-sm font-medium leading-5">
                <p>To use your IRA to fund your investment, you will need to create an IRA/Roth IRA account at Strata Trust Company. Strata is the third party trust company that is approved to invest IRAs at Mammoth due to extra reporting requirements placed on the fund.</p>

                <ul class="mt-4 ml-6 space-y-2 list-disc">
                    <li>Prior to Strata being able to pull your funds, you will need to ensure that cash is available in your IRA account. Strata will transfer this cash from your current IRA account and then forward your cash to Mammoth for investment purposes.</li>
                    <li>Once your funds are moved to cash, it may take one or more weeks to get your money fully moved to Strata and then invested at Mammoth. We cannot control the general investment markets during the timing of your transfer. You may miss out in general market growth or losses that occur while your IRA funds sit in cash during the transfer process. You agree not to hold Strata or Mammoth responsible if the general market increases during the time period when your IRA funds are sitting in cash.</li>
                    <li>Strata charges their own fee schedule for administering your IRA account. At Mammoth, we believe that Strata's fee schedule is competitive and reasonable. You'll have a chance to review the latest fee schedule in the electronic signing portion of this onboarding.</li>
                </ul>
            </div>
            <select-field v-model="modelValue.hasAccount" label="Strata Account" label-by="label" track-by="value" :options="iraStrata" :required="true"></select-field>
            <div v-if="modelValue.hasAccount == 'Yes'">
                <text-field v-model="modelValue.accountNumber" label="Account Number" :required="true"></text-field>
            </div>
        </div>

        <div v-if="modelValue.hasAccount != ''">
            <div v-if="modelValue.hasAccount == 'No'" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-1 sm:col-span-2">
                    <h3 class="text-lg font-medium leading-6 text-left no-underline normal-case non-italic">IRA/Roth IRA Information</h3>
                </div>

                <div class="relative col-span-1">
                    <div class="absolute ml-1 top-1 left-48 group">
                        <svg class="w-4 h-4 text-gray-300 hover:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                        <div class="absolute hidden p-6 mb-1 -ml-4 text-sm font-semibold text-white bg-black rounded-md group-hover:block bottom-full left-full w-80">
                            <p>This is the name of the company that holds your investment. Common Custodians are Vanguard, Schwab, TD Ameritrade, etc...</p>
                        </div>
                    </div>
                    <text-field v-model="modelValue.custodianName" label="Name of Current Custodian" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.accountName" label="Your Name as it appears on the Account" :required="true"></text-field>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <text-field v-model="modelValue.accountNumber" v-maska="'#*'" label="Account Number with Current Custodian" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <address-field v-model="modelValue.address1" @placechanged="onPlaceChanged" label="Custodian Address Line 1" :required="true"></address-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.phone" label="Custodian Phone Number" v-maska="'(###) ###-####'" :required="true"></text-field>
                </div>

                <div class="col-span-1 sm:col-span-2">
                    <text-field v-model="modelValue.address2" label="Custodian Address Line 2" :required="false"></text-field>
                </div>
            </div>

            <div v-if="modelValue.hasAccount == 'No'" class="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-3">
                <div class="col-span-1">
                    <text-field v-model="modelValue.city" label="City" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <template v-if="modelValue.country == 'US'">
                        <select-field v-model="modelValue.state" label="State" label-by="name" track-by="abbreviation" :options="states" :searchable="true" :required="true"></select-field>
                    </template>
                    <template v-else>
                        <text-field v-model="modelValue.state" label="State" :required="true"></text-field>
                    </template>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.zip" v-maska="'#*'" label="Zip Code" :required="true"></text-field>
                </div>

                <div class="col-span-1 sm:col-span-3">
                    <select-field v-model="modelValue.country" label="Country" label-by="country" track-by="abbreviation" :options="countries" :searchable="true" :required="true"></select-field>
                </div>
            </div>
        </div>

        <div v-if="modelValue.hasAccount != ''">
            <div class="relative">
                <div class="absolute ml-1 top-1 left-24 group">
                    <svg class="w-4 h-4 text-gray-300 hover:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                    <div class="absolute hidden p-6 mb-1 -ml-4 text-sm font-semibold text-white bg-black rounded-md group-hover:block bottom-full left-full w-96">
                        <h4 class="pb-2">Strata Fees as of 07/20/2021</h4>
                        <h5 class="pb-2 text-xs">One-Time</h5>

                        <div class="pb-6 prose font-normal text-xs !text-white">
                            <ul>
                                <li>$50 account setup fee</li>
                                <li>$50 Private investment purchase one-time fee (also occurs on 'sale' which isn't relevant)</li>
                            </ul>
                        </div>

                        <h5 class="pb-2 text-xs">Recurring</h5>
                        <div class="pb-6 prose font-normal text-xs !text-white">
                            <ul>
                                <li>$300 annual account fee for flex IRA (required to hold private investment; due on setup of account)</li>
                            </ul>
                        </div>

                        <div class="flex items-center mb-2 text-xs font-semibold">
                            <div class="flex-1">Total upfront fee:</div>
                            <div>$400</div>
                        </div>
                        <div class="flex items-center mb-2 text-xs font-semibold">
                            <div class="flex-1">Total annual recurring fee:</div>
                            <div>$350</div>
                        </div>
                        <p class="text-xs italic font-normal">Additional variable fees may apply.</p>
                    </div>
                </div>
                <h3 class="pb-4 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Strata Fees</h3>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-1">
                    <text-field v-model="modelValue.ccName" label="Name on Credit Card" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.ccNumber" pattern="[0-9]*" inputmode="numeric" v-maska="'#### #### #### ####'" label="Credit Card Number" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.ccExpiration" pattern="[0-9]*" inputmode="numeric" v-maska="'##/##'" label="Expiration Date" placeholder="MM/YY" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.ccVerificationCode" pattern="[0-9]*" inputmode="numeric" v-maska="'###'" label="CVC" :required="true"></text-field>
                </div>
            </div>
        </div>

        <div v-if="modelValue.hasAccount == 'No'" class="mt-20">
            <h3 class="text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Primary Beneficiary(ies)</h3>

            <div class="divide-y divide-gray-200">
                <div v-for="(beneficiary, index) in modelValue.beneficiary" :key="index" class="relative">
                    <div class="relative py-10" :class="{
                        'bg-gray-100 rounded-md !p-5 my-5': beneficiariesNum > 1
                    }">
                        <a href="#" v-show="beneficiariesNum > 1" @click.prevent="removeBeneficiary(index)" class="absolute block -top-2 -left-2"><svg class="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg></a>

                        <div class="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-3">
                            <div class="col-span-1">
                                <text-field v-model="beneficiary.firstName" :label="isMarried ? 'Spouse Legal First Name' : 'First Name'" :required="true"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.lastName" :label="isMarried ? 'Spouse Legal Last Name' : 'Last Name'" :required="true"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.nickname" :label="isMarried ? 'Spouse Nickname' : 'Nickname'" :required="false"></text-field>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div class="col-span-1">
                                <text-field v-model="beneficiary.ssn" :label="isMarried ? 'Spouse SSN or TIN' : 'SSN or TIN'" :required="false"></text-field>
                            </div>

                            <div v-if="!isMarried" class="col-span-1">
                                <text-field v-model="beneficiary.relation" label="Relationship to IRA Owner" :required="false"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.dob" v-maska="'##/##/####'" :label="isMarried ? 'Spouse Date of Birth' : 'Beneficiary Date of Birth'" placeholder="MM/DD/YYYY" :required="false"></text-field>
                            </div>

                            <div class="col-span-1 sm:col-span-3">
                                <address-field v-model="beneficiary.address" @placechanged="onBeneficiaryPlaceChanged($event, beneficiary)" :label="isMarried ? 'Spouse Address' : 'Address'" :required="true"></address-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.city" label="City" :required="true"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.state" label="State" :required="true"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.zip" pattern="[0-9]*" inputmode="numeric" v-maska="'#*'" label="Zip Code" :required="true"></text-field>
                            </div>

                            <div class="col-span-1 sm:col-span-3">
                                <range-field v-model="beneficiary.percentage" label="Percentage" :disabled="isMarried"></range-field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-10" v-if="beneficiariesNum < 4 && !isMarried">
                <app-button @click="addBeneficiary" :disabled="modelValue.beneficiary.length >= 4" class="!w-full !text-center !border-gray-300 !bg-gray-300 hover:!border-teal hover:!bg-teal">&plus; Add Beneficiary</app-button>
            </div>
        </div>
    </div>
</template>

<script>
import AppButton from '@Components/Button'
import TextField from '@Fields/Text'
import TextareaField from '@Fields/Textarea'
import SelectField from '@Fields/Select'
import CheckboxField from '@Fields/Checkbox'
import RadioField from '@Fields/Radio'
import DateField from '@Fields/Date'
import UploadsField from '@Fields/Uploads'
import RangeField from '@Fields/Range'
import AddressField from '@Fields/Address.vue'
import dataCountries from '@Data/countries'
import dataStates from '@Data/us-states'
import { useStore } from 'vuex'
import { computed, watch } from 'vue'

export default {
    components: {
        AppButton,
        TextField,
        TextareaField,
        SelectField,
        CheckboxField,
        RadioField,
        DateField,
        UploadsField,
        RangeField,
        AddressField
    },

    props: ['modelValue', 'isInvestorMarried', 'series'],

    setup(props){
        const store = useStore()

        const profileData = computed(() => {
            return store.state.profileForm
        })

        const isMarried = computed(() => {
            return props.isInvestorMarried == 'Married'
        })

        const beneficiariesNum = computed(() => {
            return props.modelValue.beneficiary.length
        })

        watch(() => props.isInvestorMarried, (state) => {
            if (state == 'Married') {
                if (beneficiariesNum.value > 1) {
                    _.each(props.modelValue.beneficiary, (item, index) => {
                    if (index > 0) {
                        props.modelValue.beneficiary.splice(index, 1)
                    }
                })
                }

                props.modelValue.beneficiary[0].percentage = 100
                props.modelValue.beneficiary[0].relation = 'Spouse'
            }
        }, {
            immediate: true
        })

        function onPlaceChanged(place){
            if (place.city) {
                props.modelValue.city = place.city
            }

            if (place.state) {
                props.modelValue.state = place.state
            }

            if (place.zip) {
                props.modelValue.zip = place.zip
            }

            if (place.country) {
                props.modelValue.country = place.country
            }
        }

        function onBeneficiaryPlaceChanged(place, beneficiary){
            if (place.city) {
                beneficiary.city = place.city
            }

            if (place.state) {
                beneficiary.state = place.state
            }

            if (place.zip) {
                beneficiary.zip = place.zip
            }
        }

        function addBeneficiary(){
            if (beneficiariesNum.value >= 4) {
                return false
            }

            props.modelValue.beneficiary.push({
                firstName: '',
                lastName: '',
                ssn: '',
                relation: '',
                dob: '',
                address: '',
                city: '',
                state: '',
                zip: '',
                percentage: 100
            })
        }

        function removeBeneficiary(index){
            props.modelValue.beneficiary.splice(index, 1)
        }

        return {
            profileData,
            isMarried,
            onPlaceChanged,
            onBeneficiaryPlaceChanged,
            beneficiariesNum,
            addBeneficiary,
            removeBeneficiary
        }
    },

    computed: {
        countries(){
            return dataCountries
        },
        states(){
            return dataStates;
        },
        iraStrata(){
            return [{
                label: 'I already have an IRA account with Strata',
                value: 'Yes'
            }, {
                label: 'I need to create an account with Strata and transfer funds to it',
                value: 'No'
            }]
        }
    }
}
</script>

<style>

</style>
